import type {
    IBufferTime,
    INoticePeriod,
    ISessionReminder,
    TSessionColors,
    TSessionKeys,
    TSessionNoticePeriodUnit,
    TSessionReminderUnit,
    TSessionRollingWindowDurationUnit,
    TSessionTimeUnit,
    TSettingOption
} from '~/apps/main/Scheduler/pages/ConfigureSession/utils/configure-session.types';
import { timezonesAsDropdownOptionsShort } from '~/utils/timezone.utils';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { useMemo } from 'react';
import {
    SessionCallerTypes,
    SessionReminderTypes,
    SessionType,
    SessionTypes,
    SessionWindowTypes,
    VideoConferenceTypes,
    type VideoConferenceType
} from '~/types/enums.types';
import {
    ICalendarProvidersResponse,
    IDayAvailability,
    IZoomAuthProviderResponse
} from '~/types/server.types';
import { padTimeWithZero } from '~/utils/date.utils';
import { TDayOfWeek } from '~/types/app.types';

export const CONFIGURE_SESSION_TEST_ID_PREFIX = 'configure-session';

export const getIsNewSessionConfig = () =>
    getSessionConfigURLParams().sessionId === 'new';

export function getSessionConfigURLParams() {
    const { 3: sessionId, 5: userId } = location.pathname.split('/');
    return { sessionId, userId };
}

export type TConfigureSessionSections =
    | 'availability'
    | 'confirmation'
    | 'details';

export const SESSION_AVAILABILITY_INCREMENTS = [
    5, 10, 15, 20, 30, 45, 60
] as const;

// TODURI: remove these?
export const SESSION_DURATION_UNITS = [
    'minutes',
    'hours'
] as const satisfies ReadonlyArray<TSessionReminderUnit>;

export const BUFFER_TIME_UNITS = SESSION_DURATION_UNITS;

export const SESSION_REMINDER_TYPES = Object.values(SessionReminderTypes);

export const SESSION_NOTICE_UNITS = [
    'hours',
    'days',
    'weeks'
] as const satisfies ReadonlyArray<TSessionNoticePeriodUnit>;

export const SESSION_REMINDER_UNITS = [
    'minutes',
    'hours',
    'days',
    'weeks'
] as const satisfies ReadonlyArray<TSessionReminderUnit>;

export const SESSION_ROLLING_WINDOW_DURATION_UNITS = [
    'days',
    'weeks',
    'months'
] as const satisfies ReadonlyArray<TSessionTimeUnit>;

export const DAY_NAMES = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
] satisfies TDayOfWeek[];

export const SHORT_ORDERED_DAY_NAMES = [
    'Mon',
    'Tue',
    'Wed',
    'Thu',
    'Fri',
    'Sat',
    'Sun'
] as const;

export const DAY_NAMES_WITH_INDEX_RECORD = DAY_NAMES.reduce(
    (acc, day, index) => {
        acc[day] = index;
        return acc;
    },
    {} as Record<TDayOfWeek, number>
);

export const SESSION_WINDOW_TYPES = Object.values(SessionWindowTypes);
export const PHONE_CALL_CALLER_TYPES = Object.values(SessionCallerTypes);
export const VIDEO_CONFERENCE_TYPES = Object.values(VideoConferenceTypes);

export const SESSION_COLOR_KEYS = [
    'purple-shade-3',
    'purple-shade-2',
    'purple',
    'muted-blue-shade-3',
    'muted-blue-shade-2',
    'muted-blue',
    'light-blue-shade-3',
    'light-blue-shade-2',
    'light-blue',
    'orange-shade-3',
    'orange-shade-2',
    'orange',
    'brown-shade-3',
    'brown-shade-2',
    'brown',
    'red-shade-3',
    'red-shade-2',
    'red',
    'pink-shade-3',
    'pink-shade-2',
    'pink'
] as const;

export const SESSION_COLORS = {
    'muted-blue-shade-2': '#3c5aa2',
    'muted-blue-shade-3': '#273a68',
    brown: '#e4c093',
    'brown-shade-2': '#ce8c39',
    'brown-shade-3': '#875a22',
    'light-blue': '#59b5ff',
    'light-blue-shade-2': '#0085f1',
    'light-blue-shade-3': '#00569b',
    'muted-blue': '#738dcb',
    orange: '#fda55b',
    'orange-shade-2': '#ee6e03',
    'orange-shade-3': '#994702',
    pink: '#ff95ed',
    'pink-shade-2': '#ff1cd8',
    'pink-shade-3': '#b60097',
    purple: '#9759ff',
    'purple-shade-2': '#5a00f1',
    'purple-shade-3': '#3a009b',
    red: '#ff5962',
    'red-shade-2': '#f1000d',
    'red-shade-3': '#9b0008'
} as const satisfies Record<TSessionColors, string>;

export const DEFAULT_TIME_RANGE = {
    range_time_end: '17:00',
    range_time_start: '09:00'
} as const;

export const DEFAULT_BUFFER_TIME = {
    unit_count: 30,
    unit_type: 'minutes'
} as const satisfies IBufferTime;

export const DEFAULT_NOTICE_PERIOD = {
    unit_count: 2,
    unit_type: 'days'
} as const satisfies INoticePeriod;

export const DEFAULT_INCREMENTS = 30 as const;

export const ADDITIONAL_SETTINGS = [
    {
        example: 'buffer_time.example',
        key: 'bufferTime' as TSettingOption,
        subtitle: 'buffer_time.subtitle',
        title: 'buffer_time.title'
    },
    {
        example: 'minimum_notice.example',
        key: 'minimumNotice' as TSettingOption,
        subtitle: 'minimum_notice.subtitle',
        title: 'minimum_notice.title'
    },
    {
        example: 'custom_increments.example',
        key: 'customIncrements' as TSettingOption,
        subtitle: 'custom_increments.subtitle',
        title: 'custom_increments.title'
    },
    {
        key: 'teamAvailability' as TSettingOption,
        subtitle: 'team_availability.subtitle',
        title: 'team_availability.title'
    }
] as const;

export const DEFAULT_ADD_REMINDER = {
    reminder_type: 'sms_and_email',
    when: {
        unit_count: 1,
        unit_type: 'hours'
    }
} as const satisfies ISessionReminder;

export const DEFAULT_REMINDERS = [
    DEFAULT_ADD_REMINDER,
    {
        reminder_type: 'sms_and_email',
        when: {
            unit_count: 1,
            unit_type: 'days'
        }
    }
] as const satisfies ReadonlyArray<ISessionReminder>;

export const CONFIRMATION_ADDITIONAL_SETTINGS = [
    {
        key: 'allow_rescheduling',
        label: 'reschedule.title',
        subLabel: 'reschedule.subtitle'
    },
    {
        key: 'allow_canceling',
        label: 'cancel.title',
        subLabel: 'cancel.subtitle'
    },
    {
        key: 'send_confirmation_email',
        label: 'confirmation_email.title',
        subLabel: 'confirmation_email.subtitle'
    }
] as const;

interface UseSessionConfigSchema {
    calendarProviderData?: ICalendarProvidersResponse;
    zoomAuthProviderData?: IZoomAuthProviderResponse;
}

export const useSessionConfigSchema = ({
    calendarProviderData,
    zoomAuthProviderData
}: UseSessionConfigSchema = {}) => {
    const { t } = useTranslation(NAMESPACES.SCHEDULER_SESSION, {
        keyPrefix: 'validation_errors'
    });

    return useMemo(
        () =>
            ({
                _id: yup.string(),
                allow_canceling: yup.boolean(),
                allow_rescheduling: yup.boolean(),
                availability_increments: yup
                    .number()
                    .nullable()
                    .oneOf([null, ...SESSION_AVAILABILITY_INCREMENTS]),
                buffer_after: getDurationSchema(SESSION_REMINDER_UNITS, {
                    unit_count: t('buffers.unit_count'),
                    max_value: t('buffers.max_value')
                }).nullable(),
                buffer_before: getDurationSchema(SESSION_REMINDER_UNITS, {
                    unit_count: t('buffers.unit_count'),
                    max_value: t('buffers.max_value')
                }).nullable(),
                confirmation_message: yup.string(),
                day_availabilities: yup.array().of(
                    yup.object().shape({
                        day_of_week: yup.string().nullable(),
                        override_date: yup.string().nullable(),
                        time_ranges: getTimeRangesSchema({
                            invalidTimeRange: t(
                                'day_availabilities.invalid_time_range'
                            ),
                            timeRangesOverlap: t(
                                'day_availabilities.time_ranges'
                            )
                        })
                    })
                ),
                enabled: yup.boolean(),
                fixed_end_date: yup
                    .string()
                    .nullable()
                    .when('window_type', {
                        is: SessionWindowTypes.FIXED_DATE_RANGE,
                        otherwise: yup.string().notRequired(),
                        then: yup.string().required(t('fixed_end_date'))
                    }),
                fixed_start_date: yup
                    .string()
                    .nullable()
                    .when('window_type', {
                        is: SessionWindowTypes.FIXED_DATE_RANGE,
                        otherwise: yup.string().notRequired(),
                        then: yup.string().required(t('fixed_start_date'))
                    }),
                meeting_location: yup.string(),
                notice_period: getDurationSchema(SESSION_NOTICE_UNITS, {
                    unit_count: t('notice_period.unit_count'),
                    max_value: t('notice_period.max_value')
                }).nullable(),
                phone_call_caller: getSessionTypeSchema(
                    'phone_call',
                    PHONE_CALL_CALLER_TYPES,
                    { general: t('phone_call_caller') }
                ),
                phone_call_number: yup.string(),
                placement_id: yup.string(),
                rolling_window_duration: yup
                    .object()
                    .when('window_type', {
                        is: 'rolling_window',
                        then: getDurationSchema(
                            SESSION_ROLLING_WINDOW_DURATION_UNITS,
                            {
                                unit_count: t(
                                    'rolling_window_duration.unit_count'
                                ),
                                unit_type: t(
                                    'rolling_window_duration.unit_type'
                                ),
                                max_value: t(
                                    'rolling_window_duration.max_value'
                                )
                            }
                        )
                    })
                    .nullable(),
                schedule_behaviour_type: yup.string().nullable(),
                send_confirmation_email: yup.boolean(),
                session_color: yup
                    .string()
                    .oneOf(SESSION_COLOR_KEYS)
                    .required(),
                session_description: yup.string(),
                session_duration: getDurationSchema(SESSION_DURATION_UNITS, {
                    unit_count: t('session_duration.unit_count'),
                    max_value: t('session_duration.max_value')
                }),
                session_name: yup
                    .string()
                    .max(64, t('session_name.max_length'))
                    .required(t('session_name.required')),
                session_reminders: yup
                    .array()
                    .nullable()
                    .max(2)
                    .of(
                        yup.object().shape({
                            _id: yup.string().nullable(),
                            id: yup.string().nullable(),
                            notify_me: yup.boolean().nullable(),
                            reminder_title: yup.string(),
                            reminder_message: yup.string().nullable(),
                            reminder_type: yup
                                .string()
                                .nullable()
                                .oneOf([null, ...SESSION_REMINDER_TYPES]),
                            when: getDurationSchema(SESSION_REMINDER_UNITS, {
                                unit_count: t(
                                    'session_reminders.when.unit_count'
                                ),
                                max_value: t('session_reminders.when.max_value')
                            }).required()
                        } satisfies Record<keyof ISessionReminder, any>)
                    ),
                session_timezone: yup
                    .string()
                    .oneOf(
                        timezonesAsDropdownOptionsShort.map(
                            ({ value }) => value
                        )
                    )
                    .typeError(t('session_timezone'))
                    .required(t('session_timezone')),
                session_type: yup
                    .string()
                    .oneOf(Object.values(SessionTypes))
                    .typeError(t('session_type'))
                    .required(t('session_type')),
                session_url: yup.string(),
                set_as_project_date: yup.boolean(),
                shared_member_ids: yup.array().when('schedule_behaviour_type', {
                    is: 'single',
                    otherwise: yup.array().of(yup.string()),
                    then: yup
                        .array()
                        .of(yup.string())
                        .min(1, t('shared_member_ids'))
                }),
                shared_members: yup.array().of(yup.object()).nullable(),
                user_id: yup.string(),
                video_conference_type: getSessionTypeSchema(
                    'video_call',
                    VIDEO_CONFERENCE_TYPES,
                    {
                        general: t('video_conference_type'),
                        google: t('google_not_signed_in'),
                        zoom: t('zoom_not_signed_in')
                    },
                    calendarProviderData,
                    zoomAuthProviderData
                ),
                video_meeting_link: yup.string(),
                window_type: yup.string().oneOf(SESSION_WINDOW_TYPES).required()
            } satisfies Record<
                Exclude<TSessionKeys, 'related_workflows' | 'updated_at'>,
                any
            >),
        [t, calendarProviderData, zoomAuthProviderData]
    );
};

function getSessionTypeSchema(
    sessionType: SessionType,
    options: ReadonlyArray<string>,
    errors?: {
        general?: string;
        google?: string;
        zoom?: string;
    },
    calendarProviderData?: ICalendarProvidersResponse,
    zoomAuthProviderData?: IZoomAuthProviderResponse
) {
    return yup
        .string()
        .nullable()
        .when('session_type' satisfies TSessionKeys, {
            is: 'video_call',
            then: yup
                .string()
                .required()
                .test('match', errors?.google, (value: VideoConferenceType) => {
                    if (value === 'google') {
                        return (
                            !!calendarProviderData &&
                            !!calendarProviderData.calendar_provider &&
                            calendarProviderData.calendar_provider._type ===
                                'GoogleCalendarProvider'
                        );
                    }

                    return true;
                })
                .test('match', errors?.zoom, value => {
                    if (value === 'zoom') {
                        return (
                            !!zoomAuthProviderData &&
                            !!zoomAuthProviderData.zoom_provider
                        );
                    }

                    return true;
                })
        })
        .when('session_type' satisfies Omit<TSessionKeys, 'video_call'>, {
            is: sessionType,
            then: yup
                .string()
                .oneOf(options)
                .nullable()
                .required(errors?.general)
        });
}

function getDurationSchema(
    options: ReadonlyArray<
        TSessionReminderUnit | TSessionRollingWindowDurationUnit
    >,
    error?: {
        unit_count?: string;
        unit_type?: string;
        max_value?: string;
    },
    required: boolean = false
) {
    const schema = yup.object().shape({
        _id: yup.string().nullable(),
        unit_count: yup
            .number()
            .integer()
            .min(1, error?.unit_count)
            .max(999, error?.max_value)
            .typeError(error?.unit_count)
            .required(error?.unit_count),
        unit_type: yup.string().oneOf(options).required(error?.unit_type)
    });

    return required ? schema.required() : schema.nullable();
}

export function getTimeRangesSchema(errors: {
    invalidTimeRange: string;
    timeRangesOverlap: string;
}) {
    return yup
        .array()
        .of(
            yup.object().shape({
                range_time_end: yup.string().required(),
                range_time_start: yup.string().required()
            })
        )
        .test(
            'match',
            errors.timeRangesOverlap,
            (timeRanges: IDayAvailability['time_ranges']) =>
                !verifyValidityOfTimeRanges(timeRanges)
        )
        .test(
            'match',
            errors.timeRangesOverlap,
            (timeRanges: IDayAvailability['time_ranges']) =>
                !verifyDoTimeRangesOverlap(timeRanges)
        );
}

export const verifyDoTimeRangesOverlap = (
    timeRanges: IDayAvailability['time_ranges']
) => {
    // there's no overlap if there's no time ranges or just one
    if (timeRanges.length < 2) {
        return false;
    }

    // otherwise compare and identify at least one overlap
    for (let i = 0; i < timeRanges.length; i++) {
        for (let j = 0; j < timeRanges.length; j++) {
            // do not compare the same time range
            if (i == j) {
                continue;
            }

            const startTime = timeRanges[i].range_time_start;
            const endTime = timeRanges[i].range_time_end;

            // times ranges overlap if either start or end time is within any other time range
            if (
                // is start time within the range of another time range
                (padTimeWithZero(startTime) >=
                    padTimeWithZero(timeRanges[j].range_time_start) &&
                    padTimeWithZero(startTime) <=
                        padTimeWithZero(timeRanges[j].range_time_end)) ||
                // is end time within the range of another time range
                (padTimeWithZero(endTime) >=
                    padTimeWithZero(timeRanges[j].range_time_start) &&
                    padTimeWithZero(endTime) <=
                        padTimeWithZero(timeRanges[j].range_time_end))
            ) {
                return true;
            }
        }
    }

    return false;
};

export const verifyValidityOfTimeRanges = (
    timeRanges: IDayAvailability['time_ranges']
) => {
    for (const timeRange of timeRanges) {
        if (
            padTimeWithZero(timeRange.range_time_start) >=
            padTimeWithZero(timeRange.range_time_end)
        ) {
            return true;
        }
    }

    return false;
};
